// ─── ROUTING ───────────────────────────────────────────────────
const LEGACY_ADMIN_SCREEN_PATHS = {
  'admin-users': '/users',
  'support-admin': '/support',
  'delivery-logs': '/delivery-logs',
  'admin-ocf': '/ocf',
};

const maybeRedirectLegacyAdminPath = () => {
  const segment = window.location.pathname.replace(/^\//, '').split('/')[0];
  const adminPath = LEGACY_ADMIN_SCREEN_PATHS[segment];
  if (adminPath) window.location.href = `https://admin.omnifill.net${adminPath}`;
};

maybeRedirectLegacyAdminPath();

const DASHBOARD_FEATURE_CHUNKS = [
  '02-sidebar.js',
  '03-sources.js',
  '04-products.js',
  '05-orders.js',
  '06-export.js',
  '07-settings.js',
  '08-analytics.js',
  '09-home.js',
  '10-support.js',
  '14-setup-guide.js',
  '15-assistant.js',
];

const DASHBOARD_FEATURE_VERSION_QUERY = (() => {
  const src = document.currentScript?.getAttribute('src') || '';
  const queryStart = src.indexOf('?');
  return queryStart >= 0 ? src.slice(queryStart) : '';
})();

let dashboardFeatureChunksPromise = null;

const loadDashboardFeatureScript = file => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[src*="/assets/dashboard-compiled/${file}"]`);
  if (existing) {
    resolve();
    return;
  }

  const script = document.createElement('script');
  script.src = `/assets/dashboard-compiled/${file}${DASHBOARD_FEATURE_VERSION_QUERY}`;
  script.async = false;
  script.dataset.dashboardFeatureChunk = file;
  script.onload = () => resolve();
  script.onerror = () => reject(new Error(`Could not load ${file}`));
  document.body.appendChild(script);
});

const loadDashboardFeatureChunks = () => {
  if (!dashboardFeatureChunksPromise) {
    dashboardFeatureChunksPromise = DASHBOARD_FEATURE_CHUNKS.reduce(
      (chain, file) => chain.then(() => loadDashboardFeatureScript(file)),
      Promise.resolve()
    ).catch(error => {
      dashboardFeatureChunksPromise = null;
      throw error;
    });
  }
  return dashboardFeatureChunksPromise;
};

const useDashboardFeatureChunks = enabled => {
  const [status, setStatus] = useState(enabled ? 'loading' : 'idle');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    if (!enabled) {
      setStatus('idle');
      setError('');
      return () => { active = false; };
    }

    setStatus('loading');
    setError('');
    loadDashboardFeatureChunks()
      .then(() => {
        if (active) setStatus('ready');
      })
      .catch(err => {
        if (!active) return;
        setError(err?.message || 'Dashboard assets could not load.');
        setStatus('error');
      });

    return () => { active = false; };
  }, [enabled]);

  return {
    ready: !enabled || status === 'ready',
    loading: enabled && status === 'loading',
    error,
  };
};

const VALID_SCREENS = ['home', 'setup', 'sources', 'orders', 'products', 'analytics', 'export', 'support', 'settings', 'adobe-commerce-setup', 'adobe-commerce-identity'];

const pathToScreen = (pathname) => {
  const cleanPath = pathname.replace(/\/+$/, '') || '/';
  if (/^\/connectors\/adobe-commerce\/setup\/\d+$/i.test(cleanPath)) return 'adobe-commerce-setup';
  if (/^\/connectors\/adobe-commerce\/identity$/i.test(cleanPath)) return 'adobe-commerce-identity';
  const segment = cleanPath.replace(/^\//, '').split('/')[0];
  if (!segment) return 'home';
  return VALID_SCREENS.includes(segment) ? segment : 'home';
};

const ONBOARDING_FOCUS_KEY = 'omnifill_onboarding_focus';
const ONBOARDING_FOCUS_TARGETS = {
  'connect-source': ['Connect with Stripe', 'Connect with PayPal', 'Connect with Shopify', 'Connect with WooCommerce', 'Connect with Adobe Commerce', 'Upload CSV'],
  'manual-order': ['Add order'],
  'new-product': ['Product'],
  'map-products': ['Define', 'Assign', 'Aliases'],
  'new-rule': ['New rule'],
  'send-batch': ['Download CSV and mark Routed', 'Set up ShipBob', 'Set up SFTP', 'Set up DCIx'],
  'view-activity': ['View activity'],
};

const readOnboardingFocus = () => {
  try {
    const focus = JSON.parse(sessionStorage.getItem(ONBOARDING_FOCUS_KEY) || 'null');
    if (!focus?.screen || !focus?.focus) return null;
    if (Date.now() - Number(focus.createdAt || 0) > 15 * 60 * 1000) {
      sessionStorage.removeItem(ONBOARDING_FOCUS_KEY);
      return null;
    }
    return focus;
  } catch { return null; }
};

const clearOnboardingFocus = () => {
  try { sessionStorage.removeItem(ONBOARDING_FOCUS_KEY); } catch {}
};

const normalizeButtonText = value => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

const isVisibleOnboardingTarget = element => {
  if (!element || element.disabled || element.getAttribute('aria-disabled') === 'true') return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const readSetupGuideDismissed = () => {
  try { return localStorage.getItem(SETUP_GUIDE_DISMISSED_KEY) === 'true'; } catch { return false; }
};

const writeSetupGuideDismissed = value => {
  try { localStorage.setItem(SETUP_GUIDE_DISMISSED_KEY, value ? 'true' : 'false'); } catch {}
};

const DEFAULT_SOURCE_SYNC_SETTINGS = { enabled:true, intervalMinutes:10 };

// ─── DASHBOARD SHELL ───────────────────────────────────────────
const Dashboard = ({ tweaks, setTweak, user, onLogout, onUserChange }) => {
  const [screen, setScreen] = useState(() => pathToScreen(window.location.pathname));
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [exportRules, setExportRules] = useState([]);
  const [exportRulesLoaded, setExportRulesLoaded] = useState(false);
  const [loadingExportRules, setLoadingExportRules] = useState(true);
  const [lastSettledSetupProgress, setLastSettledSetupProgress] = useState(null);
  const [setupGuideDismissed, setSetupGuideDismissed] = useState(() => readSetupGuideDismissed());
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sourceSyncSettings, setSourceSyncSettings] = useState(DEFAULT_SOURCE_SYNC_SETTINGS);
  const ordersRequestIdRef = useRef(0);

  useEffect(() => {
    const openAssistant = () => setAssistantOpen(true);
    window.addEventListener('omnifill:open-assistant', openAssistant);
    return () => window.removeEventListener('omnifill:open-assistant', openAssistant);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [mobileNavOpen]);

  const [syncing, setSyncing] = useState(false);
  const productScreens = ['setup', 'orders', 'products', 'analytics', 'export', 'support'];

  const fetchOrders = useCallback(async () => {
    const requestId = ordersRequestIdRef.current + 1;
    ordersRequestIdRef.current = requestId;
    setOrdersError('');
    try {
      const result = await api('GET', '/api/orders');
      if (requestId !== ordersRequestIdRef.current) return result;
      if (result.ok) setOrders(Array.isArray(result.data) ? result.data : []);
      else setOrdersError(result.data?.error || 'Could not load orders.');
      setLoadingOrders(false);
      return result;
    } catch (error) {
      if (requestId === ordersRequestIdRef.current) {
        setOrdersError('Could not load orders.');
        setLoadingOrders(false);
      }
      return { ok:false, data:null, error };
    }
  }, []);

  const fetchProducts = useCallback((force = false) => {
    if (!force && productsLoaded) return Promise.resolve();
    setLoadingProducts(true);
    return api('GET', '/api/products').then(({ ok, data }) => {
      if (ok) setProducts(data);
      if (ok) setProductsLoaded(true);
      setLoadingProducts(false);
    }).catch(() => {
      setLoadingProducts(false);
    });
  }, [productsLoaded]);

  const fetchExportRules = useCallback(() => {
    setLoadingExportRules(true);
    return api('GET', '/api/export-rules').then(({ ok, data }) => {
      setExportRules(ok && Array.isArray(data?.rules) ? data.rules : []);
      setExportRulesLoaded(true);
      setLoadingExportRules(false);
    }).catch(() => {
      setExportRulesLoaded(true);
      setLoadingExportRules(false);
    });
  }, []);

  const fetchSourceSyncSettings = useCallback(() => (
    api('GET', '/api/source-sync/settings').then(({ ok, data }) => {
      if (ok) setSourceSyncSettings(data || DEFAULT_SOURCE_SYNC_SETTINGS);
    }).catch(() => {})
  ), []);

  const refreshOrdersAndProducts = useCallback(() => {
    fetchOrders();
    fetchProducts(true);
    onUserChange?.();
  }, [fetchOrders, fetchProducts, onUserChange]);

  const refreshFulfillmentSetup = useCallback(() => {
    refreshOrdersAndProducts();
    fetchExportRules();
  }, [fetchExportRules, refreshOrdersAndProducts]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchExportRules(); }, [fetchExportRules]);
  useEffect(() => { fetchSourceSyncSettings(); }, [fetchSourceSyncSettings]);
  useEffect(() => {
    if ((screen === 'setup' || screen === 'export') && exportRulesLoaded) fetchExportRules();
  }, [screen, fetchExportRules, exportRulesLoaded]);
  useEffect(() => {
    if (productScreens.includes(screen)) fetchProducts();
  }, [screen, fetchProducts]);

  const syncLiveSources = useCallback(async ({ dueOnly = false } = {}) => {
    setSyncing(true);
    let syncError = '';
    try {
      const { ok: syncOk, data: syncData } = await api('POST', '/api/source-sync/run', { dueOnly });
      if (syncOk && syncData?.settings) setSourceSyncSettings(syncData.settings);
      if (syncOk && syncData?.ok === false && !dueOnly) syncError = syncData?.lastError || 'Some sources could not sync.';
      if (!syncOk && !dueOnly) syncError = syncData?.error || 'Could not sync sources.';
    } catch {
      if (!dueOnly) syncError = 'Could not sync sources.';
    }
    const ordersResult = await fetchOrders();
    if (ordersResult?.ok && syncError) setOrdersError(syncError);
    if (productScreens.includes(screen)) fetchProducts(true);
    onUserChange?.();
    setSyncing(false);
  }, [fetchOrders, fetchProducts, onUserChange, screen]);

  useEffect(() => {
    if (sourceSyncSettings?.enabled === false) return undefined;
    const intervalMinutes = Math.max(1, Number(sourceSyncSettings?.intervalMinutes || DEFAULT_SOURCE_SYNC_SETTINGS.intervalMinutes) || DEFAULT_SOURCE_SYNC_SETTINGS.intervalMinutes);
    const id = setInterval(() => syncLiveSources({ dueOnly:true }), intervalMinutes * 60 * 1000);
    return () => clearInterval(id);
  }, [sourceSyncSettings?.enabled, sourceSyncSettings?.intervalMinutes, syncLiveSources]);

  useEffect(() => {
    if (screen === 'orders' && !loadingOrders) syncLiveSources({ dueOnly:false });
  }, [screen, loadingOrders]);

  useEffect(() => {
    const onPopState = () => setScreen(pathToScreen(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Keep URL in sync with active screen
  useEffect(() => {
    const current = pathToScreen(window.location.pathname);
    if (current !== screen) {
      if (screen === 'adobe-commerce-setup' || screen === 'adobe-commerce-identity') return;
      history.pushState({}, '', screen === 'home' ? '/' : `/${screen}`);
    }
  }, [screen]);

  useEffect(() => {
    const focus = readOnboardingFocus();
    if (focus && focus.screen !== screen && screen !== 'setup') {
      clearOnboardingFocus();
      return;
    }
    if (!focus || focus.screen !== screen) return;

    const labels = ONBOARDING_FOCUS_TARGETS[focus.focus] || [];
    if (!labels.length) return;

    let highlighted = [];
    let observer = null;
    let attempts = 0;
    const normalizedLabels = labels.map(normalizeButtonText);

    const cleanupHighlights = () => {
      highlighted.forEach(element => {
        element.classList.remove('onboarding-action-highlight');
        element.removeEventListener('click', handleTargetClick);
      });
      highlighted = [];
    };

    function handleTargetClick() {
      cleanupHighlights();
      clearOnboardingFocus();
      observer?.disconnect();
    }

    const applyHighlight = () => {
      cleanupHighlights();
      const root = document.querySelector('[data-dashboard-content]') || document;
      const candidates = Array.from(root.querySelectorAll('button, a')).filter(element => {
        const text = normalizeButtonText(element.textContent || element.getAttribute('aria-label') || element.getAttribute('title'));
        return isVisibleOnboardingTarget(element) && normalizedLabels.some(label => text.includes(label));
      });

      highlighted = candidates.slice(0, focus.focus === 'connect-source' ? 5 : 1);
      highlighted.forEach(element => {
        element.classList.add('onboarding-action-highlight');
        element.addEventListener('click', handleTargetClick, { once:true });
      });
      if (highlighted[0]) highlighted[0].scrollIntoView({ block:'nearest', inline:'nearest', behavior:'smooth' });
      return highlighted.length > 0;
    };

    const retry = () => {
      attempts += 1;
      const found = applyHighlight();
      if (found || attempts >= 16) observer?.disconnect();
    };

    const timeout = setTimeout(retry, 180);
    observer = new MutationObserver(retry);
    const content = document.querySelector('[data-dashboard-content]');
    if (content) observer.observe(content, { childList:true, subtree:true });

    return () => {
      clearTimeout(timeout);
      observer?.disconnect();
      cleanupHighlights();
    };
  }, [screen, loadingOrders, loadingProducts, orders.length, products.length]);

  const handleUpdateOrder = (id, patch) => {
    const nextPatch = typeof patch === 'string' ? { status: patch } : (patch || {});
    setOrders(prev => prev.map(o => o.id === id ? { ...o, ...nextPatch } : o));
  };

  const handleBulkUpdateOrders = (ids, patch) => {
    const idSet = new Set(Array.isArray(ids) ? ids : []);
    if (!idSet.size) return;
    setOrders(prev => prev.map(order => idSet.has(order.id) ? { ...order, ...(patch || {}) } : order));
  };

  const productImageById = new Map(products.map(product => [String(product.id), product.image_data || '']));
  const ordersWithProductImages = orders.map(order => ({
    ...order,
    items: Array.isArray(order.items)
      ? order.items.map(item => ({
          ...item,
          product_image_data: item.product_id ? productImageById.get(String(item.product_id)) || null : null,
        }))
      : order.items,
    product_image_data: order.product_id ? productImageById.get(String(order.product_id)) || null : null,
  }));
  const activeOrdersWithProductImages = ordersWithProductImages.filter(order => !isIgnoredDashboardOrder(order));
  const activeOrderCount = activeOrdersWithProductImages.length;
  const currentMonthStart = new Date();
  currentMonthStart.setDate(1);
  currentMonthStart.setHours(0, 0, 0, 0);
  const monthlyOrdersUsedFromOrders = ordersWithProductImages.reduce((count, order) => {
    if (order.deleted) return count;
    const rawDate = order.created_at || order.createdAt || '';
    const parsed = rawDate ? new Date(String(rawDate).replace(' ', 'T')) : null;
    return parsed && !Number.isNaN(parsed.getTime()) && parsed >= currentMonthStart ? count + 1 : count;
  }, 0);
  const planState = user?.plan || {};
  const isFreeWorkspace = planState.plan === 'free' && !planState.paid;
  const freeOrderLimit = Number(planState.orderLimit || 0);
  const planOrdersUsed = Number(planState.ordersUsed);
  const freeOrdersUsed = Math.max(
    0,
    Number.isFinite(planOrdersUsed) ? Math.max(planOrdersUsed, monthlyOrdersUsedFromOrders) : monthlyOrdersUsedFromOrders
  );
  const freeOrderPct = freeOrderLimit ? Math.min(100, Math.round((freeOrdersUsed / freeOrderLimit) * 100)) : 0;
  const freeOrderLimitReached = !!freeOrderLimit && freeOrdersUsed >= freeOrderLimit;
  const freeOrderUsageColor = freeOrderLimitReached ? '#B91C1C' : freeOrderPct >= 80 ? '#92400E' : '#64748B';
  const freeOrderUsageBar = freeOrderLimitReached ? '#DC2626' : freeOrderPct >= 80 ? '#F59E0B' : '#006FDD';
  const setupOrdersLoading = loadingOrders && ordersWithProductImages.length === 0;
  const setupProductsLoading = loadingProducts && !productsLoaded;
  const setupRulesLoading = loadingExportRules && !exportRulesLoaded;
  const setupGuideSteps = buildSetupGuideSteps({
    orders: ordersWithProductImages,
    products,
    rules: exportRules,
    ordersLoading: setupOrdersLoading,
    productsLoading: setupProductsLoading,
    rulesLoading: setupRulesLoading,
  });
  const rawSetupGuideProgress = setupGuideProgressFromSteps(setupGuideSteps);
  useEffect(() => {
    if (!rawSetupGuideProgress.loading) setLastSettledSetupProgress(rawSetupGuideProgress);
  }, [rawSetupGuideProgress.done, rawSetupGuideProgress.total, rawSetupGuideProgress.loading]);
  const setupGuideProgress = rawSetupGuideProgress.loading && lastSettledSetupProgress
    ? { ...lastSettledSetupProgress, loading:true, stale:true }
    : rawSetupGuideProgress;
  const dismissSetupGuide = () => {
    setSetupGuideDismissed(true);
    writeSetupGuideDismissed(true);
    if (screen === 'setup') setScreen('home');
  };

  const screenMap = {
    home:      <HomeScreen user={user} orders={activeOrdersWithProductImages} ordersLoading={loadingOrders} ordersError={ordersError} onNavigate={setScreen} onRefresh={refreshOrdersAndProducts} onUserChange={onUserChange}/>,
    setup:     <SetupGuideScreen orders={ordersWithProductImages} products={products} rules={exportRules} ordersLoading={setupOrdersLoading} productsLoading={setupProductsLoading} rulesLoading={setupRulesLoading} onNavigate={setScreen} onDismiss={dismissSetupGuide}/>,
    sources:   <SourcesScreen orders={ordersWithProductImages} ordersLoading={loadingOrders} onOrdersChanged={refreshOrdersAndProducts} onNavigate={setScreen} autoSyncSettings={sourceSyncSettings} onAutoSyncSettingsChange={setSourceSyncSettings}/>,
    orders:    <OrdersScreen orders={ordersWithProductImages} products={products} ordersError={ordersError} onUpdateOrder={handleUpdateOrder} onBulkUpdateOrders={handleBulkUpdateOrders} onProductsChanged={refreshOrdersAndProducts} compact={tweaks.tableCompact} showRaw={tweaks.showRawData} onRefresh={() => syncLiveSources({ dueOnly:false })} syncing={syncing} dateFormat={tweaks.dateFormat} onSetDateFormat={v => setTweak('dateFormat', v)} onNavigate={setScreen}/>,
    products:  <ProductsScreen user={user} products={products} orders={ordersWithProductImages} ordersLoading={loadingOrders || loadingProducts} onProductsChanged={refreshOrdersAndProducts}/>,
    analytics: <AnalyticsScreen products={products}/>,
    export:    <ExportScreen orders={ordersWithProductImages} products={products} ordersLoading={loadingOrders} onOrdersChanged={refreshFulfillmentSetup} onNavigate={setScreen}/>,
    support:   <SupportScreen user={user} screen={screen} orders={ordersWithProductImages} products={products}/>,
    settings:  <SettingsScreen user={user} onUserChange={onUserChange} onNavigate={setScreen}/>,
    'adobe-commerce-setup': <AdobeCommerceSetupScreen onNavigate={setScreen}/>,
    'adobe-commerce-identity': <AdobeCommerceIdentityScreen onNavigate={setScreen}/>,
  };
  const contentBackground = screen === 'home'
    ? 'radial-gradient(circle at 50% -8%, rgba(0,111,221,0.10), transparent 34%), radial-gradient(circle at 84% 18%, rgba(115,208,75,0.07), transparent 24%), linear-gradient(180deg, #FBFCFE 0%, #F6F8FB 100%)'
    : 'transparent';
  const assistantContext = {
    screen,
    setupProgress: setupGuideProgress,
    loading: {
      orders: setupOrdersLoading,
      products: setupProductsLoading,
      exportRules: setupRulesLoading,
    },
    counts: {
      orders: setupOrdersLoading ? null : activeOrderCount,
      products: setupProductsLoading ? null : products.length,
      exportRules: setupRulesLoading ? null : exportRules.length,
      setupDone: setupGuideProgress.done,
      setupTotal: setupGuideProgress.total,
    },
  };

  const navigateFromSidebar = nextScreen => {
    setMobileNavOpen(false);
    setScreen(nextScreen);
  };

  return (
    <div className="dashboard-shell">
      <button type="button" className={`dashboard-nav-scrim${mobileNavOpen ? ' is-visible' : ''}`} onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" tabIndex={mobileNavOpen ? 0 : -1}/>
      <Sidebar active={screen} onNavigate={navigateFromSidebar} sidebarBg={tweaks.sidebarBg} user={user} onLogout={onLogout} mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} setupGuide={{ dismissed:setupGuideDismissed, progress:setupGuideProgress, onDismiss:dismissSetupGuide }}/>
      <div className="dashboard-main">
        <div className="dashboard-topbar">
          <div className="dashboard-topbar-start">
            <button type="button" className="dashboard-menu-button" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation" aria-expanded={mobileNavOpen ? 'true' : 'false'}>
              <span/><span/><span/>
            </button>
          <a className="dashboard-homepage-link" href="https://omnifill.net" style={{ color:'#6B7280', fontSize:12, display:'flex', alignItems:'center', gap:5, textDecoration:'none' }}>
            <ChevronLeft size={13} strokeWidth={2}/> Homepage
          </a>
          </div>
          <div className="dashboard-topbar-meta" style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:12, minWidth:0, maxWidth:'min(78vw, 860px)' }}>
            <button type="button" className="assistant-topbar-button" onClick={() => setAssistantOpen(true)} title="Open Omnifill assistant">
              <Sparkles size={14} strokeWidth={2}/><span>AI Assistant</span>
            </button>
            {isFreeWorkspace && freeOrderLimit > 0 && (
              <>
                <div className="dashboard-topbar-divider" style={{ width:1, height:14, background:'#E5E7EB', flexShrink:0 }}/>
                <button
                  type="button"
                  className="dashboard-plan-usage"
                  onClick={() => setScreen('settings')}
                  title={`Starter order usage: ${freeOrdersUsed} of ${freeOrderLimit} this month`}
                  style={{ border:'none', background:'transparent', padding:0, cursor:'pointer', display:'flex', alignItems:'center', gap:8, minWidth:0, color:freeOrderUsageColor }}
                >
                  <span style={{ fontSize:12, fontWeight:800, whiteSpace:'nowrap' }}>Starter</span>
                  <span style={{ display:'flex', alignItems:'center', gap:6, minWidth:0 }}>
                    <span style={{ fontSize:12, fontWeight:800, fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>{freeOrdersUsed}/{freeOrderLimit} orders</span>
                    <span aria-hidden="true" style={{ width:42, height:3, borderRadius:4, background:'#E5E7EB', overflow:'hidden', flexShrink:0 }}>
                      <span style={{ display:'block', width:`${freeOrderPct}%`, height:'100%', borderRadius:4, background:freeOrderUsageBar }}></span>
                    </span>
                  </span>
                </button>
              </>
            )}
            {user?.email && (
              <div className="dashboard-user-email" title={user.email} style={{ minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:12, color:'#6B7280', fontWeight:600 }}>
                {user.email}
              </div>
            )}
            {screen !== 'sources' && (
              <>
                <div className="dashboard-topbar-divider" style={{ width:1, height:14, background:'#E5E7EB', flexShrink:0 }}/>
                <div className="dashboard-order-summary" style={{ minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:12, color:'#9CA3AF' }}>
                  {loadingOrders
                    ? <SkeletonPill width={128} height={13}/>
                    : activeOrderCount > 0
                      ? `${activeOrderCount} active order${activeOrderCount!==1?'s':''}`
                      : ordersError
                      ? 'Orders could not load'
                      : 'No active orders'}
                </div>
              </>
            )}
          </div>
        </div>
        <div data-dashboard-content="true" style={{ flex:1, overflow: screen === 'support' ? 'hidden' : 'auto', background:contentBackground }}>
          {loadingOrders && screen === 'orders'
            ? <div style={{ padding:48, textAlign:'center', color:'#9CA3AF', fontSize:13 }}>Loading orders…</div>
            : screenMap[screen]
          }
        </div>
      </div>
      <AssistantDrawer open={assistantOpen} onClose={() => setAssistantOpen(false)} screen={screen} onNavigate={setScreen} dashboardContext={assistantContext}/>
    </div>
  );
};

// ─── APP ROOT ──────────────────────────────────────────────────
const App = () => {
  const { user, loading, signup, login, verifyEmail, verifyMfa, transferAuth, resendVerification, forgotPassword, resetPassword, logout, refreshAuth } = useAuth();
  const { tweaks, setTweak, TweaksPanel } = useTweaks(TWEAK_DEFAULTS);
  const dashboardFeatures = useDashboardFeatureChunks(!!user);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') !== 'google') return;
    window.OmnifillAnalytics?.trackLead?.({
      content_name: 'Google signup',
      status: 'verified',
    });
    window.OmnifillAnalytics?.trackRegistration?.({
      content_name: 'Google signup',
    });
    params.delete('signup');
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, [user?.email]);

  if (loading) {
    return <DashboardLoader/>;
  }

  if (!user) {
    return (
      <AuthScreen
        onSignup={(email, password, intent) => signup(email, password, intent)}
        onLogin={(identifier, password) => login(identifier, password)}
        onVerifyEmail={(email, code) => verifyEmail(email, code)}
        onVerifyMfa={(payload) => verifyMfa(payload)}
        onTransferAuth={(token) => transferAuth(token)}
        onResendVerification={(email) => resendVerification(email)}
        onForgotPassword={(email) => forgotPassword(email)}
        onResetPassword={(email, code, password) => resetPassword(email, code, password)}
      />
    );
  }

  if (!dashboardFeatures.ready) {
    if (dashboardFeatures.error) {
      return (
        <div style={{ minHeight:'100vh', background:'#F8F9FA', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div style={{ width:'100%', maxWidth:420, background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:24, textAlign:'center' }}>
            <h1 style={{ margin:'0 0 8px', fontSize:18, fontWeight:900, color:'#111827' }}>Dashboard could not load</h1>
            <p style={{ margin:'0 0 16px', fontSize:13, lineHeight:1.5, color:'#6B7280' }}>{dashboardFeatures.error}</p>
            <button type="button" onClick={() => window.location.reload()} style={{ background:'#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'10px 13px', fontSize:13, fontWeight:900, cursor:'pointer' }}>Refresh</button>
          </div>
        </div>
      );
    }
    return <DashboardLoader/>;
  }

  return (
    <>
      <Dashboard tweaks={tweaks} setTweak={setTweak} user={user} onLogout={logout} onUserChange={refreshAuth}/>
      <TweaksPanel>
        <TweakSection title="Colors">
          <TweakColor id="accentBlue"  label="Primary Blue"/>
          <TweakColor id="accentGreen" label="Accent Green"/>
          <TweakColor id="sidebarBg"   label="Sidebar"/>
        </TweakSection>
        <TweakSection title="Orders Table">
          <TweakToggle id="tableCompact" label="Compact rows"/>
          <TweakToggle id="showRawData"  label="Show raw data in drawer"/>
        </TweakSection>
      </TweaksPanel>
    </>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(<App/>);
